import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CompanyLocationsListPreviewOutDTO,
  FeaturesEnum,
  LocationRampTableOutDTO,
  LocRampTableDataOutDTO,
} from '@api/ringil-api.v2';
import clsx from 'clsx';
import SvgAngleDown from '@icons/angle-down.svg?react';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface YardTableProps {
  locations: CompanyLocationsListPreviewOutDTO[];
}

export const YardTable: React.FC<YardTableProps> = ({ locations }) => {
  const { tr } = useTranslationLgs();
  const { getLocRampTable, removeFromLocRampTable } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);


  const [tableData, setTableData] = useState<LocRampTableDataOutDTO[]>(undefined);
  const [hasTableData, setHasTableData] = useState(false);
  const [yardTableOpened, setYardTableOpened] = useState(false);

  const yardTableEnabled = loggedUser?.features?.some(t => t === FeaturesEnum.YARD_TABLE);

  useEffect(() => {
    refresh();
  }, [locations]);

  const refresh = () => {
    if (!yardTableEnabled || !locations?.length) return;

    Promise.all(
      locations.map(location =>
        new Promise<LocationRampTableOutDTO>(resolve =>
          getLocRampTable(location.locationId, data => resolve(data)),
        ),
      ),
    ).then(allData => {
      const hasAnyData = allData.some(d =>
        d?.definedArea?.currentData && d.definedArea.currentData.length > 0,
      );

      const combinedData: LocRampTableDataOutDTO[] = allData.flatMap(d =>
        d?.definedArea?.currentData ?? [],
      );

      setTableData(combinedData);
      setHasTableData(hasAnyData);
    });
  };

  const removeFromTable = (reservationId: string, locationId: string) => {
    removeFromLocRampTable(reservationId, locationId, () => {
      refresh();
    });
  };


  if (!tableData) return null;

  return (
    <div className="pb-4 w-[200px]">
      <div
        onClick={() => setYardTableOpened(!yardTableOpened)}
        className={clsx(
          'flex flex-row justify-between cursor-pointergrow rounded-lg cursor-pointer border border-solid px-4 py-3 bg-white leading-[1.125rem] hover:border-content-brand',
          !yardTableOpened ? 'border-content-brandMedium' : 'border-content-brand',
        )}
      >
        <div className="text-content-gr4 flex flex-wrap gap-2 min-w-fit">
          {tr(`YardTable.attachedOnTable`, `Attached on table`)}
        </div>

        <SvgAngleDown className={clsx('transition-all', yardTableOpened ? 'rotate-180' : 'rotate-0')} />
      </div>
      <div
        className={clsx(
          yardTableOpened ? 'block' : 'hidden',
        )}
      >
        <div className="flex flex-col w-80 bg-black gap-2 mt-2">
          {tableData?.map((rowData, rowKey) => (
            <div key={rowKey}>
              <div
                className="flex text-[#ff0000] font-[700] text-[19px] justify-between p-[0.3rem] font-['Courier_New']">
                {rowData.text}
                <span
                  className="pi pi-trash cursor-pointer"
                  onClick={() => removeFromTable(rowData.reservationId, rowData.locationId)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
